import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    hasImageOverlay = "hasImageOverlay",
    imageOverlayType = "imageOverlayType",
    imageOverlayColor = "imageOverlayColor",
    backgroundImagePositionX = "backgroundImagePositionX",
    backgroundImagePositionY = "backgroundImagePositionY",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    minimumHeight = "minimumHeight",
    customMinHeight = "customMinHeight",
    maxWidth = "maxWidth",
    customMaxWidth = "customMaxWidth",
    verticalAligment = "verticalAligment",
    horizontalAligment = "horizontalAligment",
    textAlignment = "textAlignment",
    hasBorder = "hasBorder",
    borderColor = "borderColor",
    borderWidth = "borderWidth",
    isBorderWidthAdvancedOptions = "isBorderWidthAdvancedOptions",
    borderWidthTop = "borderWidthTop",
    borderWidthRight = "borderWidthRight",
    borderWidthBottom = "borderWidthBottom",
    borderWidthLeft = "borderWidthLeft",
    borderRadius = "borderRadius",
    isBorderRadiusAdvancedOptions = "isBorderRadiusAdvancedOptions",
    borderRadiusTopLeft = "borderRadiusTopLeft",
    borderRadiusTopRight = "borderRadiusTopRight",
    borderRadiusBottomRight = "borderRadiusBottomRight",
    borderRadiusBottomLeft = "borderRadiusBottomLeft",
    contentPadding = "contentPadding",
    isPaddingAdvancedOptions = "isPaddingAdvancedOptions",
    paddingTop = "paddingTop",
    paddingRight = "paddingRight",
    paddingBottom = "paddingBottom",
    paddingLeft = "paddingLeft",
    hasMobileLayoutOptions = "hasMobileLayoutOptions",
    mobileImageOverlayType = "mobileImageOverlayType",
    mobileImageOverlayColor = "mobileImageOverlayColor",
    mobileContentPadding = "mobileContentPadding",
    isMobilePaddingAdvancedOptions = "isMobilePaddingAdvancedOptions",
    mobilePaddingTop = "mobilePaddingTop",
    mobilePaddingRight = "mobilePaddingRight",
    mobilePaddingBottom = "mobilePaddingBottom",
    mobilePaddingLeft = "mobilePaddingLeft",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.hasImageOverlay]: boolean;
        [fields.imageOverlayType]: "plain" | "gradient";
        [fields.imageOverlayColor]: string;
        [fields.backgroundImagePositionX]: number;
        [fields.backgroundImagePositionY]: number;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.minimumHeight]: "None" | "1/4" | "1/2" | "3/4" | "full" | "custom";
        [fields.customMinHeight]: number;
        [fields.maxWidth]: "fullWidth" | "fitContent" | "custom";
        [fields.customMaxWidth]: number;
        [fields.verticalAligment]: "flex-start" | "center" | "flex-end" | "space-between";
        [fields.horizontalAligment]: "flex-start" | "center" | "flex-end";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.hasBorder]: boolean;
        [fields.borderColor]: string;
        [fields.borderWidth]: number;
        [fields.isBorderWidthAdvancedOptions]: boolean;
        [fields.borderWidthTop]: number;
        [fields.borderWidthRight]: number;
        [fields.borderWidthBottom]: number;
        [fields.borderWidthLeft]: number;
        [fields.borderRadius]: number;
        [fields.isBorderRadiusAdvancedOptions]: boolean;
        [fields.borderRadiusTopLeft]: number;
        [fields.borderRadiusTopRight]: number;
        [fields.borderRadiusBottomRight]: number;
        [fields.borderRadiusBottomLeft]: number;
        [fields.contentPadding]: number;
        [fields.isPaddingAdvancedOptions]: boolean;
        [fields.paddingTop]: number;
        [fields.paddingRight]: number;
        [fields.paddingBottom]: number;
        [fields.paddingLeft]: number;
        [fields.hasMobileLayoutOptions]: boolean;
        [fields.mobileImageOverlayType]: "plain" | "gradient";
        [fields.mobileImageOverlayColor]: string;
        [fields.mobileContentPadding]: number;
        [fields.isMobilePaddingAdvancedOptions]: boolean;
        [fields.mobilePaddingTop]: number;
        [fields.mobilePaddingRight]: number;
        [fields.mobilePaddingBottom]: number;
        [fields.mobilePaddingLeft]: number;
    };
}

export interface StyledContainerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
}

export const styledContainerStyles: StyledContainerStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: stretch;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = styledContainerStyles;

export const StyledContainer: FC<OwnProps> = ({ id, fields }) => {
    const isEditing = useContext(ShellContext).isEditing || false;

    const backgroundStyles = css`
        ${fields.background === "image"
            ? css`
                background-color: transparent;
                background-image: url(${fields.image});
                background-size: cover;
                background-position: center;
            `
        : null}
        ${fields.background === "color"
            ? css`
                background-color: ${fields.backgroundColor};
                background-size: auto;
                background-image: none;
            `
        : null}
    `;

    const resolveMinHeight = () => {
        switch (fields.minimumHeight) {
            case "None":
                return "none";
            case "1/4":
                return "25vh";
            case "1/2":
                return "50vh";
            case "3/4":
                return "75vh";
            case "full":
                return "100vh";
            case "custom":
                return `${fields.customMinHeight}px`;
        }
    };

    const minimumHeightStyles = css`
        ${`min-height: ${resolveMinHeight()};`}
    `;

    let widthStyles = css`
        width: 100%;
    `;

    if (fields.maxWidth === "fitContent") {
        widthStyles = css`
            width: auto;
        `;
    }

    if (fields.maxWidth === "custom") {
        widthStyles = css`
            width: 100%;
            max-width: ${`${fields.customMaxWidth}px`};
        `;
    }

    const borderWidth = css`
        ${fields.hasBorder
            ? css`
                border-style: solid;
                border-color: ${fields.borderColor};

                ${fields.isBorderWidthAdvancedOptions
                    ? css`
                        border-top-width: ${fields.borderWidthTop}px;
                        border-right-width: ${fields.borderWidthRight}px;
                        border-bottom-width: ${fields.borderWidthBottom}px;
                        border-left-width: ${fields.borderWidthLeft}px;
                    `
                    : css`
                        border-width: ${fields.borderWidth}px;
                    `
                }
            `
        : null}
    `;

    const borderRadius = css`
        ${fields.isBorderRadiusAdvancedOptions
            ? css`
                border-top-left-radius: ${fields.borderRadiusTopLeft}px;
                border-top-right-radius: ${fields.borderRadiusTopRight}px;
                border-bottom-right-radius: ${fields.borderRadiusBottomRight}px;
                border-bottom-left-radius: ${fields.borderRadiusBottomLeft}px;
            `
            : css`
                border-radius: ${fields.borderRadius}px;
            `}
    `;

    const layoutStyles = {
        css: css`
            ${styles.wrapper?.css}
            ${backgroundStyles}
            ${minimumHeightStyles}
            ${widthStyles}
            ${borderRadius}
            ${borderWidth}
        `
    };

    const fieldsPadding = css`
        ${fields.isPaddingAdvancedOptions
            ? css`
                padding-top: ${fields.paddingTop}px;
                padding-right: ${fields.paddingRight}px;
                padding-bottom: ${fields.paddingBottom}px;
                padding-left: ${fields.paddingLeft}px;
            `
            : css`
                padding ${fields.contentPadding}px;
            `
        }

        ${fields.hasMobileLayoutOptions
            ? fields.isMobilePaddingAdvancedOptions
                ? css`
                    ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding-top: ${fields.mobilePaddingTop}px;
                                    padding-right: ${fields.mobilePaddingRight}px;
                                    padding-bottom: ${fields.mobilePaddingBottom}px;
                                    padding-left: ${fields.mobilePaddingLeft}px;
                                `,
                                css`
                                    padding-top: ${fields.mobilePaddingTop}px;
                                    padding-right: ${fields.mobilePaddingRight}px;
                                    padding-bottom: ${fields.mobilePaddingBottom}px;
                                    padding-left: ${fields.mobilePaddingLeft}px;
                                `,
                                null,
                                null,
                                null,
                            ])
                        }
                `
                : css`
                    ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    padding ${fields.mobileContentPadding}px;
                                `,
                                css`
                                    padding ${fields.mobileContentPadding}px;
                                `,
                                null,
                                null,
                                null,
                            ])
                        }
                `
            : null
        }
    `;

    const responsiveOverlayWrapperStyles = css`
        ${fields.background === "image" && fields.hasImageOverlay
            ? css`
                ${fields.imageOverlayType === "plain" &&
                css`
                    background-color: ${fields.imageOverlayColor};
                `}

                ${fields.imageOverlayType === "gradient" &&
                css`
                    background: linear-gradient(-90deg, rgba(32, 56, 50, 0) 0%, ${fields.imageOverlayColor} 100%);
                `}
            `
            : null}

            ${fields.mobileImageOverlayType ? css`
                ${fields.mobileImageOverlayType === "plain" &&
                css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                background-color: ${fields.mobileImageOverlayColor};
                            `,
                            css`
                                background-color: ${fields.mobileImageOverlayColor};
                            `,
                            null,
                            null,
                            null,
                        ])
                    }
                `}

                ${fields.mobileImageOverlayType === "gradient" &&
                    css`
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                background: linear-gradient(180deg, rgba(32, 56, 50, 0) 0%, ${fields.mobileImageOverlayColor} 100%);
                            `,
                            css`
                                background: linear-gradient(180deg, rgba(32, 56, 50, 0) 0%, ${fields.mobileImageOverlayColor} 100%);
                            `,
                            null,
                            null,
                            null,
                        ])
                    }
                `}
                ` : null}
    `;


const overlayWrapperStyles = {
    css: css`
        ${styles.overlayWrapper?.css}
        ${!isEditing
            ? css`
                    display: flex;
                `
            : null}
        ${fieldsPadding}
        ${borderRadius}
        ${responsiveOverlayWrapperStyles}
        align-items: ${fields.verticalAligment};
        justify-content: ${fields.horizontalAligment};
        color: ${fields.textColor};
        text-align: ${fields.textAlignment};
        `,
    };

    const slideCenteringWrapperStyles = {
        ...styles.slideCenteringWrapperStyles,
        css: css`
            ${styles.slideCenteringWrapperStyles?.css}
            ${widthStyles}

            ${fields.verticalAligment === "space-between"
                ? css`
                    height: 100%;

                    > [class^=Wrapper] {
                        display: flex;
                        flex-flow: column;
                        justify-content: space-between;
                        height: 100%;
                    }
                `
                : null}
        `,
    };

    return (
        <StyledWrapper {...layoutStyles} className="StyledContainer">
            <StyledWrapper {...overlayWrapperStyles}>
                <StyledWrapper {...slideCenteringWrapperStyles}>
                    <Zone contentId={id} zoneName="containerZone" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const layoutTab = {
    displayName: "Layout",
    sortOrder: 1,
}


const mobileTab = {
    displayName: "Mobile",
    sortOrder: 2,
};

const widget: WidgetModule = {
    component: StyledContainer,
    definition: {
        group: "Basic",
        displayName: "Styled Container",
        icon: "Banner",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.verticalAligment,
                displayName: "Content Vertical Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Bottom", value: "flex-end" },
                    { displayName: "* Space-Between", value: "space-between" },
                ],
                defaultValue: "center",
                tooltip: "* Advanced option - has impact on other properties.",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.horizontalAligment,
                displayName: "Content Horizontal Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.textAlignment,
                displayName: "Overall Text Aligment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Middle", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.minimumHeight,
                displayName: "Minimum Container Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "None", value: "none" },
                    { displayName: "1/4 Viewport", value: "1/4" },
                    { displayName: "1/2 Viewport", value: "1/2" },
                    { displayName: "3/4 Viewport", value: "3/4" },
                    { displayName: "Full Viewport", value: "full" },
                    { displayName: "Full Height (of parent)", value: "fullParent" },
                    { displayName: "Custom", value: "custom" },
                ],
                defaultValue: "None",
                hideEmptyOption: true,
                tooltip: "The value is a percentage of the current viewport height",
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.customMinHeight,
                displayName: "Custom Min Height",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: layoutTab,
                isVisible: item => item.fields.minimumHeight === "custom",
            },
            {
                fieldType: "General",
                name: fields.maxWidth,
                displayName: "Maximum Container Width",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Full Width", value: "fullWidth" },
                    { displayName: "Fit Content", value: "fitContent" },
                    { displayName: "Custom", value: "custom" },
                ],
                defaultValue: "fullWidth",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "General",
                name: fields.customMaxWidth,
                displayName: "Custom Max Width",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                tab: layoutTab,
                isVisible: item => item.fields.maxWidth === "custom",
            },
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "",
                hideEmptyOption: false,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.hasImageOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageOverlayType,
                displayName: "Image Color Overlay",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Gradient", value: "gradient" },
                    { displayName: "Plain", value: "plain" },
                ],
                defaultValue: "gradient",
                hideEmptyOption: false,
                isVisible: widget => widget.fields.background === "image" && widget.fields.hasImageOverlay,
            },
            {
                fieldType: "General",
                name: fields.imageOverlayColor,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image" && widget.fields.hasImageOverlay,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: fields.hasBorder,
                displayName: "Show Border?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                fieldType: "General",
                isVisible: widget => widget.fields.hasBorder,
            },
            {
                name: fields.borderWidth,
                displayName: "Border Width",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => !widget.fields.isBorderWidthAdvancedOptions && widget.fields.hasBorder,
            },
            {
                name: fields.isBorderWidthAdvancedOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget => widget.fields.hasBorder,
            },
            {
                name: fields.borderWidthTop,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
            },
            {
                name: fields.borderWidthRight,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
            },
            {
                name: fields.borderWidthBottom,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
            },
            {
                name: fields.borderWidthLeft,
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderWidthAdvancedOptions,
            },
            {
                name: fields.borderRadius,
                displayName: "Border Radius",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isVisible: widget => !widget.fields.isBorderRadiusAdvancedOptions,
            },
            {
                name: fields.isBorderRadiusAdvancedOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.borderRadiusTopLeft,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
            },
            {
                name: fields.borderRadiusTopRight,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
            },
            {
                name: fields.borderRadiusBottomRight,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
            },
            {
                name: fields.borderRadiusBottomLeft,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                isVisible: widget => widget.fields.isBorderRadiusAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => !widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.isPaddingAdvancedOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                fieldType: "General",
                name: fields.paddingTop,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingRight,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.paddingLeft,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isPaddingAdvancedOptions,
            },
            {
                fieldType: "General",
                name: fields.hasMobileLayoutOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobileImageOverlayType,
                displayName: "Mobile Image Color Overlay",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Gradient", value: "gradient" },
                    { displayName: "Plain", value: "plain" },
                ],
                defaultValue: "gradient",
                hideEmptyOption: false,
                isVisible: widget => widget.fields.background === "image" && widget.fields.hasMobileLayoutOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobileImageOverlayColor,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image" && widget.fields.hasMobileLayoutOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobileContentPadding,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => !widget.fields.isMobilePaddingAdvancedOptions && widget.fields.hasMobileLayoutOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.isMobilePaddingAdvancedOptions,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: widget => widget.fields.hasMobileLayoutOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobilePaddingTop,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isMobilePaddingAdvancedOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobilePaddingRight,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isMobilePaddingAdvancedOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobilePaddingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isMobilePaddingAdvancedOptions,
                tab: mobileTab,
            },
            {
                fieldType: "General",
                name: fields.mobilePaddingLeft,
                editorTemplate: "IntegerField",
                defaultValue: 50,
                isVisible: widget => widget.fields.isMobilePaddingAdvancedOptions,
                tab: mobileTab,
            },
        ],
    },
};

export default widget;
